import * as yup from 'yup'
import { TFunction } from 'i18next'

export const getTaskValidationSchema = (t: TFunction) =>
    yup.lazy((task: object) =>
        yup.object().shape({
            ...(task.hasOwnProperty('app_name')
                ? {
                      app_name: yup
                          .string()
                          .nullable()
                          .required(t('campaign.template.downloadAppName.errorMessage.empty')),
                  }
                : {}),
            ...(task.hasOwnProperty('products')
                ? {
                      products: yup
                          .array()
                          .of(
                              yup.object({
                                  values: yup.object({
                                      name: yup.string(),
                                      price: yup.string(),
                                  }),
                              }),
                          )
                          .test({
                              name: 'not-empty',
                              message: () => t('campaign.template.productDetails.errorMessage.empty'),
                              test: products => {
                                  if (!products.length) {
                                      return false
                                  }
                                  for (let i = 0; i < products.length; i++) {
                                      if (!products[i]?.value?.price || !products[i]?.value?.name) {
                                          return false
                                      }
                                  }
                                  return true
                              },
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('purchase_locations')
                ? {
                      purchase_locations: yup
                          .array()
                          .of(
                              yup.object({
                                  value: yup.string().nullable(),
                              }),
                          )
                          .test({
                              name: 'not-empty',
                              message: () => t('campaign.template.purchaseLocation.errorMessage.empty'),
                              test: locations => {
                                  for (let i = 0; i < locations.length; i++) {
                                      if (locations[i].value) {
                                          return true
                                      }
                                  }
                                  return false
                              },
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('min_purchase_quantity')
                ? { min_purchase_quantity: yup.number().nullable().required() }
                : {}),
            ...(task.hasOwnProperty('example_medias')
                ? {
                      example_medias: yup.array().of(
                          yup.object({
                              public_id: yup.string(),
                              resource_type: yup.string(),
                              file_format: yup.string(),
                          }),
                      ),
                  }
                : {}),
            example_media_description: yup.string().notRequired(),
            ...(task.hasOwnProperty('unique_selling_point')
                ? {
                      unique_selling_point: yup
                          .array()
                          .of(
                              yup.object({
                                  value: yup.string().nullable(),
                              }),
                          )
                          .test({
                              name: 'not-empty',
                              message: () => t('campaign.template.generalKeyPoints.errorMessage.empty'),
                              test: points => {
                                  for (let i = 0; i < points.length; i++) {
                                      if (points[i].value) {
                                          return true
                                      }
                                  }

                                  return false
                              },
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('caption_hashtags')
                ? {
                      caption_hashtags: yup
                          .array()
                          .of(
                              yup.object({
                                  value: yup.string(),
                              }),
                          )
                          .test({
                              name: 'no-special-characters',
                              message: () => t('campaign.template.profileTag.errorMessage.wrongFormate'),
                              test: caption_hashtag => {
                                  const noSpecialCharTest = /[^a-zA-Z\d]/g
                                  for (let i = 0; i < caption_hashtag.length; i++) {
                                      if (
                                          caption_hashtag[i].value &&
                                          noSpecialCharTest.test(caption_hashtag[i].value)
                                      ) {
                                          return false
                                      }
                                  }
                                  return true
                              },
                          })
                          .notRequired(),
                  }
                : {}),
            ...(task.hasOwnProperty('brand_handles')
                ? {
                      brand_handles: yup
                          .array()
                          .of(
                              yup.object({
                                  value: yup.string(),
                              }),
                          )
                          .test({
                              name: 'no-special-characters',
                              message: () => t('campaign.template.profileTag.errorMessage.wrongFormate'),
                              test: brand_handles => {
                                  const noSpecialCharTest = /[^a-zA-Z\d\.\_]/g
                                  for (let i = 0; i < brand_handles.length; i++) {
                                      if (brand_handles[i].value && noSpecialCharTest.test(brand_handles[i].value)) {
                                          return false
                                      }
                                  }
                                  return true
                              },
                          })
                          .notRequired(),
                  }
                : {}),
            ...(task.hasOwnProperty('download_links')
                ? {
                      download_links: yup
                          .array()
                          .of(
                              yup.object({
                                  values: yup.object({
                                      platform: yup.string(),
                                      link: yup.string(),
                                  }),
                              }),
                          )
                          .test({
                              name: 'link-format',
                              message: () => t('campaign.template.downloadAppFrom.errorMessage.wrongFormate'),
                              test: data => {
                                  for (let i = 0; i < data?.length; i++) {
                                      const linkPattern = /((https|http)?:\/\/[^\s]+\.[\w]{2,3})/g
                                      if (data?.[i].value?.link && !linkPattern.test(data[i].value?.link)) {
                                          return false
                                      }
                                  }

                                  return true
                              },
                          })
                          .test({
                              name: 'link-required',
                              message: () => t('campaign.template.downloadAppFrom.errorMessage.empty'),
                              test: data => {
                                  for (let i = 0; i < data?.length; i++) {
                                      if (data?.[i].value?.link) {
                                          return true
                                      }
                                  }

                                  return false
                              },
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('review_platform')
                ? {
                      review_platform: yup
                          .string()
                          .nullable()
                          .required(
                              t(
                                  'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformName.emptyErrorMessage',
                              ),
                          ),
                  }
                : {}),
            ...(task.hasOwnProperty('review_link')
                ? {
                      review_link: yup
                          .string()
                          .nullable()
                          .required(t('campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.emptyError'))
                          .test({
                              name: 'link-required',
                              message: () => t('campaign.template.downloadAppFrom.errorMessage.wrongFormate'),
                              test: value => {
                                  const linkPattern = /((https|http)?:\/\/[^\s]+\.[\w]{2,3})/g
                                  if (linkPattern.test(value)) {
                                      return true
                                  }

                                  return false
                              },
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('sticker_link')
                ? {
                      sticker_link: yup
                          .string()
                          .nullable()
                          .required(t('campaign.template.instagramStoryNonUGC.linkStickers.errorMessage.empty'))
                          .test({
                              name: 'link-format',
                              message: () =>
                                  t('campaign.template.step2.reviewTextImageWithApprovalTask.errorMessage.wrongFormat'),
                              test: data => {
                                  const linkPattern = /((https|http)?:\/\/[^\s]+\.[\w]{2,3})/g
                                  return linkPattern.test(data)
                              },
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('customized_sticker_text')
                ? {
                      customized_sticker_text: yup
                          .string()
                          .nullable()
                          .when('customized_sticker_text_toggle', {
                              is: true, // only validate if this field is true
                              then: yup
                                  .string()
                                  .max(
                                      46,
                                      t(
                                          'campaign.template.instagramStoryNonUGC.customisedStickerTextInput.errorMessage.overTextLimit',
                                      ),
                                  )
                                  .required(
                                      t(
                                          'campaign.template.instagramStoryNonUGC.customisedStickerTextInput.errorMessage.empty',
                                      ),
                                  ),
                              otherwise: yup.string().nullable(), // no validation if the toggle is false
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('sample_links')
                ? {
                      sample_links: yup
                          .array()
                          .of(
                              yup.object({
                                  value: yup.string().nullable(),
                              }),
                          )
                          .test({
                              name: 'link-format',
                              message: () =>
                                  t('campaign.template.step2.reviewTextImageWithApprovalTask.errorMessage.wrongFormat'),
                              test: value => {
                                  for (let i = 0; i < value?.length; i++) {
                                      const linkPattern = /((https|http)?:\/\/[^\s]+\.[\w]{2,3})/g
                                      if (value?.[i]?.value && !linkPattern.test(value[i].value)) {
                                          return false
                                      }
                                  }

                                  return true
                              },
                          })
                          .test({
                              name: 'input-required',
                              message: () => t('campaign.template.instagramStoryNonUGC.link.errorMessage.empty'),
                              test: value => {
                                  for (let i = 0; i < value?.length; i++) {
                                      if (value?.[i].value) {
                                          return true
                                      }
                                  }

                                  return false
                              },
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('shipping_fee')
                ? {
                      shipping_fee: yup
                          .number()
                          .nullable()
                          .min(0, t('campaign.template.priceInput.negativePrice'))
                          .required(t('campaign.template.shippingFee.emptyErrorMessage')),
                  }
                : {}),
            ...(task.hasOwnProperty('follow_platform')
                ? {
                      follow_platform: yup
                          .string()
                          .nullable()
                          .required(
                              t(
                                  'campaign.template.step2.reviewTextImageWithApprovalTaskPlatformName.emptyErrorMessage',
                              ),
                          ),
                  }
                : {}),
            ...(task.hasOwnProperty('follow_platform_link')
                ? {
                      follow_platform_link: yup
                          .string()
                          .nullable()
                          .required(t('campaign.template.step2.reviewTextImageWithApprovalTaskPlatformURL.emptyError'))
                          .test({
                              name: 'link-required',
                              message: () => t('campaign.template.downloadAppFrom.errorMessage.wrongFormate'),
                              test: value => {
                                  const linkPattern = /((https|http)?:\/\/[^\s]+\.[\w]{2,3})/g
                                  if (linkPattern.test(value)) {
                                      return true
                                  }

                                  return false
                              },
                          }),
                  }
                : {}),
            ...(task.hasOwnProperty('number_of_videos')
                ? {
                      number_of_videos: yup
                          .number()
                          .nullable()
                          .required(t('campaign.template.step2.videoCreation.numberOfVideo.placeholder'))
                          .test({
                              name: 'positive-integer',
                              message: () => t('errorMessage.validationPostiveInteger'),
                              test: value => {
                                  return Number.isInteger(value) && value > 0
                              },
                          }),
                  }
                : {}),
            mandatory_guidelines: yup.string().notRequired(),
        }),
    )
